import type { ObjectId } from 'mongodb';

import type { IsoDateTimeString } from './Helpers';

export interface IdProviderUser {
  emails?: string[];
  displayName?: string;
  legalName?: string;
  photoUrl?: string;
  idProviderId?: string;
  idProviderVerificationDate?: string;
}

export enum SalesRepStatus {
  active = 'active',
  /** @deprecated Use `terminated` or `suspended`. Retained for legacy data and verification-system flows. */
  blocked = 'blocked',
  /** @deprecated Use `suspended`. Retained for legacy data. */
  temporarySuspend = 'temporarySuspend',
  /** @deprecated Verification-system value only; not user-selectable. */
  override = 'override',
  /** @deprecated Verification-system value only; not user-selectable. */
  stale = 'stale',
  watchlist = 'watchlist',
  noNewSales = 'noNewSales',
  suspended = 'suspended',
  terminated = 'terminated',
}

export const salesRepStatusLabels: Record<SalesRepStatus, string> = {
  [SalesRepStatus.active]: 'Active',
  [SalesRepStatus.blocked]: 'Blocked',
  [SalesRepStatus.temporarySuspend]: 'Temp. Suspension',
  [SalesRepStatus.override]: 'Override',
  [SalesRepStatus.stale]: 'Stale',
  [SalesRepStatus.watchlist]: 'Watchlist',
  [SalesRepStatus.noNewSales]: 'No New Sales',
  [SalesRepStatus.suspended]: 'Suspended',
  [SalesRepStatus.terminated]: 'Terminated',
};

export enum SalesRepRestrictionTier {
  none = 'none',
  preNtpBlock = 'preNtpBlock',
  preInstallMilestoneBlock = 'preInstallMilestoneBlock',
}

export const salesRepStatusRestrictionTier: Record<SalesRepStatus, SalesRepRestrictionTier> = {
  [SalesRepStatus.active]: SalesRepRestrictionTier.none,
  [SalesRepStatus.watchlist]: SalesRepRestrictionTier.none,
  [SalesRepStatus.override]: SalesRepRestrictionTier.none,
  [SalesRepStatus.stale]: SalesRepRestrictionTier.none,
  [SalesRepStatus.noNewSales]: SalesRepRestrictionTier.preNtpBlock,
  [SalesRepStatus.suspended]: SalesRepRestrictionTier.preInstallMilestoneBlock,
  [SalesRepStatus.terminated]: SalesRepRestrictionTier.preInstallMilestoneBlock,
  // Legacy: map to closest equivalent so old data still enforces.
  [SalesRepStatus.blocked]: SalesRepRestrictionTier.preInstallMilestoneBlock,
  [SalesRepStatus.temporarySuspend]: SalesRepRestrictionTier.preInstallMilestoneBlock,
};

export const salesRepStatusModalSelectableStatuses: SalesRepStatus[] = [
  SalesRepStatus.active,
  SalesRepStatus.watchlist,
  SalesRepStatus.noNewSales,
  SalesRepStatus.suspended,
];

export const salesRepStatusModalHelperText: Partial<Record<SalesRepStatus, string>> = {
  [SalesRepStatus.active]: 'Rep is fully active with no restrictions.',
  [SalesRepStatus.watchlist]: 'Rep is flagged for monitoring. No account restrictions apply.',
  [SalesRepStatus.noNewSales]:
    'Rep cannot progress pre-NTP accounts. Accounts with a signed contract are unaffected.',
  [SalesRepStatus.suspended]:
    'Rep cannot progress pre-Install accounts. Accounts past the Install milestone are unaffected.',
};

export interface PreviousProviderAuthentication {
  idProvider: string;
  idProviderToken: string;
  idProviderUser: IdProviderUser;
}

/** One Recheck license result for a state; the list is append-only, so the latest entry per state is current. */
export interface SalesRepLicense {
  number: string | null;
  state: string;
  status: string | null;
  expirationDate: string | null;
  /** When the Recheck call that produced this entry ran. */
  checkedAt: IsoDateTimeString;
}

export interface SalesRepDocument {
  id: ObjectId;
  accountIds?: string[];
  idProvider: string;
  idProviderToken?: string;
  idProviderUser: IdProviderUser;
  accountEmails?: (string | undefined)[];
  organizationIds?: string[];
  status: SalesRepStatus;
  authenticatedOn?: Date;
  statusUpdatedAt?: Date;
  previousAuthentications?: PreviousProviderAuthentication[];
  licenses?: SalesRepLicense[];
}

export interface SalesRep {
  id: string;
  accountIds?: string[];
  idProvider: string;
  idProviderToken: string;
  idProviderUser: IdProviderUser;
  accountEmails?: (string | undefined)[];
  organizationIds?: string[];
  status: SalesRepStatus;
  authenticatedOn?: Date;
  previousAuthentications?: PreviousProviderAuthentication[];
  licenses?: SalesRepLicense[];
}

export enum SalesRepSearchField {
  accountEmails = 'accountEmails',
  idProviderDisplayName = 'idProviderUser.displayName',
  idProviderEmails = 'idProviderUser.emails',
  idProviderId = 'idProviderUser.idProviderId',
  idProviderLegalName = 'idProviderUser.legalName',
  organizationIds = 'organizationIds',
  status = 'status',
}

export enum SalesRepCheckReasonCode {
  emailMissing = 'SALES_REP_EMAIL_MISSING',
  notVerified = 'SALES_REP_NOT_VERIFIED',
  reconnectRequired = 'SALES_REP_RECONNECT_REQUIRED',
  blocked = 'SALES_REP_BLOCKED',
  licenseMissing = 'SALES_REP_LICENSE_MISSING',
  licenseInvalid = 'SALES_REP_LICENSE_INVALID',
  licenseCheckUnavailable = 'SALES_REP_LICENSE_CHECK_UNAVAILABLE',
}

export interface SalesRepCheckReason {
  code: SalesRepCheckReasonCode;
  message: string;
  resolution: string;
  details?: { licenseNumber?: string; licenseStatus?: string; expirationDate?: string };
}

export enum SalesRepVerificationOverrideReason {
  businessDecision = 'businessDecision',
  technicalIssues = 'technicalIssues',
}

export type SalesLicenseCheckStatus = 'valid' | 'invalid' | 'missing';

export interface SalesLicense {
  number: string | null;
  state: string;
  status: string | null;
  expirationDate: string | null;
}

export type SalesLicenseCheckResult =
  | {
      outcome: 'ok';
      status: SalesLicenseCheckStatus;
      license: SalesLicense | null;
    }
  | { outcome: 'unavailable' };
